import React, { FC, useState, useEffect, useMemo, useCallback } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { clone, uniqueId } from 'lodash'
import { useAlert } from 'react-alert'
import Tips from '../../../components/common/Tips'
import LoadingSquare from '../../../components/common/LoadingSquare'
import DateModule from '../../../components/task/MonthCalendarModule'
import {
  buildWorkTime,
  dateManipulateDay,
  dateToTodayElapsedSeconds,
  endOfMonth,
  endOfWeek,
  format,
  nowDate,
  secToHour,
  secToHourMinute,
  secToMinute,
  secToSec,
  secToTwoDigitsHourMinute,
  secToTwoDigitsSec,
  startOfMonth,
  startOfWeek,
} from '../../../../utils/date'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import {
  createTaskApprovalApplication,
  fetchTaskApprovalApplication,
  fetchTaskApprovalApplicationDetail,
} from '../../../../states/containers/task/taskSlice'
import Holidays from 'date-holidays'
import PieChart from '../../../components/task/PieChart'
import { getGetPrams, setPath, setUrlId } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import ApprovedModal from '../../../components/task/ApprovedModal'
import { labelColor, statusText } from '../../../../utils/taskStatusLabel'
import MultiApprovedModal from '../../../components/task/MultiApprovedModal'
import CsvModule from '../../../components/task/CsvModule'
import UserGuide from '../../../components/common/UserGuide'
import { updateTaskApplication } from '../../../../states/containers/common/currentSlice'
import VideoGuide from '../../../components/common/VideoGuide'

interface DetailParam {
  member_id: number
  member_name: string
  target_date: string
}

interface UpdateParam {
  member_id: number
  status: string
  comment: string
  target_date: string
  work_start_time?: string
  work_end_time?: string
}

interface GetParams {
  date: string
}

const SUMMARY_TYPE_LIST = [
  ["all"],
  ["processing", "approved", "completed"],
  ["approved", "completed"],
  ["completed"],
]

const Detail: FC = () => {
  const dispatch = useDispatch()
  const alert = useAlert()
  const location = useLocation()
  const history = useHistory()
  const { id } = useParams<{ id: string }>()

  const {
    task,
    current,
    login,
  } = useSelector((state: RootState) => state)

  const holidays = new Holidays()
  holidays.init('JP')

  const [date, setDate] = useState<string>("")
  const [checkList, setCheckList] = useState<string[]>([])
  const [isOpenDetailModal, setIsOpenDetailModal] = useState<boolean>(false)
  const [isOpenMultiApprovalModal, setIsOpenMultiApprovalModal] = useState<boolean>(false)
  const [isDisplayLiteList, setIsDisplayLiteList] = useState(false)
  const [selectMemberId, setSelectMemberId] = useState<number>(0)
  const [isOpenSelectMember, setIsOpenSelectMember] = useState<boolean>(false)
  const [summarySelect, setSummarySelect] = useState<string[]>(["processing", "approved", "completed"])
  const [isOpenSummarySelect, setIsOpenSummarySelect] = useState<boolean>(false)

  const [detailData, setDetailData] = useState<DetailParam>({
    member_id: 0,
    member_name: "",
    target_date: "",
  })

  const targetMonthData = useMemo(() => {
    const monthFirstWeekDay = startOfWeek(startOfMonth(date || nowDate()))
    const monthLastWeekDay = endOfWeek(endOfMonth(date || nowDate()))
    const dateList = []
    for (let d = clone(new Date(monthFirstWeekDay));d <= new Date(monthLastWeekDay);d.setDate(d.getDate() + 1)) {
      dateList.push(format(d, "YYYY-MM-DD"))
    }
    return Array.from(new Set(dateList))
  }, [date])

  const targetDataList = useMemo(() => {
    if (!task.taskApprovalApplication) return []
    const tmpMember = task.taskApprovalApplication?.members[0]
    if (!tmpMember) return []
    return targetMonthData.map((v) => {
      const tmpData = tmpMember.date_list.find((w) => w.date === v)
      if (tmpData) {
        return (
          {
            ...tmpData,
            member_id: tmpMember.member_id,
            member_name: tmpMember.name,
            member_email: tmpMember.email,
            member_code: tmpMember.code,
            weekday: tmpData.weekday,
            work_location_status: tmpData.work_location_status,
            approval_status: tmpData.approval_status,
            approval_progress: tmpData.approval_progress,
            team_name: tmpMember.team_name,
          }
        )
      }
      return (
        {
          member_id: tmpMember.member_id,
          member_name: tmpMember.name,
          member_email: tmpMember.email,
          member_code: tmpMember.code,
          team_name: tmpMember.team_name,
          approval_application_id: null,
          date: v,
          approval_status: "",
          approval_progress: 0,
          labeling_status: {
            labeling: 0,
            not_labeling: 0,
            start_time: null,
            end_time: null,
            break_time: 0,
            over_time: 0,
            changed: false,
            total_work_time: 0,
            not_working_time: 0,
          },
          weekday: "",
          work_location_status: null,
        }
      )
    })
  }, [targetMonthData, task.taskApprovalApplication])

  const member_id = useMemo(() => {
    if (!id) {
      return login.current_member_id
    }
    return Number(id)
  }, [id])

  const isAccessSelf = useMemo(() => (
    !!location.pathname.match(URL.TASK_APPROVAL_SELF.path)
  ), [location])

  const targetMonthSummary = useMemo(() => {
    if (!targetDataList) return {}
    let targetList = targetDataList.filter((v) => summarySelect.includes(v.approval_status))
    if (summarySelect.includes("all")) targetList = targetDataList.filter((v) => v.approval_status)
    return (
      {
        // eslint-disable-next-line max-len
        work_time: targetList.reduce((s, v) => s + (dateToTodayElapsedSeconds(v.labeling_status.end_time || nowDate(), v.date) - dateToTodayElapsedSeconds(v.labeling_status.start_time || nowDate(), v.date) - v.labeling_status.break_time), 0),
        break_time: targetList.reduce((s, v) => s + v.labeling_status.break_time, 0),
        over_time: targetList.reduce((s, v) => s + v.labeling_status.over_time, 0),
        labeling_time: targetList.reduce((s, v) => s + v.labeling_status.labeling, 0),
        not_labeling_time: targetList.reduce((s, v) => s + v.labeling_status.not_labeling, 0),
        day_count: targetList.length,
      }
    )
  }, [targetDataList, summarySelect])

  const calendarColor = (day: string, bg = false) => {
    if (Number.isNaN(new Date(day).getTime())) return bg ? "#FFFFFF" : "#666666"
    const holiday = holidays.isHoliday(new Date(day))
    if ((holiday && holiday[0].type === "public") || format(day, 'd') === '0') {
      return bg ? "#fff7f7" : "#FF0000"
    }
    if (format(day, 'd') === '6') {
      return bg ? "#f1f9ff" : "#317AB7"
    }
    return bg ? "#FFFFFF" : "#666666"
  }

  const toggleCheckDate = (targetDate: string) => {
    if (!targetDate) return
    if (checkList.includes(targetDate)) {
      setCheckList(checkList.filter((v) => v !== targetDate))
    } else {
      setCheckList([...checkList, targetDate])
    }
  }

  const checkAll = () => {
    if (isAccessSelf) {
      setCheckList(targetDataList.filter((v) => (
        (v.labeling_status.labeling || v.labeling_status.not_labeling)
        && ["unapplied", "returned", "dropped", "canceled"].includes(v.approval_status)
      )).map((v) => v.date))
    } else {
      setCheckList(targetDataList.filter((v) => (
        (v.labeling_status.labeling || v.labeling_status.not_labeling)
        && v.approval_status === "processing"
      )).map((v) => v.date))
    }
  }

  const resetCheckList = () => {
    setCheckList([])
  }

  const toggleListView = () => {
    setIsDisplayLiteList(!isDisplayLiteList)
  }

  const dayOfWeek = (day: string | undefined) => {
    if (!day || Number.isNaN(new Date(day).getTime())) return ""
    const holiday = holidays.isHoliday(new Date(day))
    let dd = format(day, "dd", "ja")
    if (holiday && holiday[0].type === "public") {
      dd = "祝"
    }
    return (
      <>
        (
        <span style={{ color: calendarColor(day) }}>
          {dd}
        </span>
        )
      </>
    )
  }

  const buildDate = (day: string | undefined) => {
    if (!day || Number.isNaN(new Date(day).getTime())) return ""
    return (
      <>
        {format(day, "YYYY年M月D日")}
        &nbsp;
        {dayOfWeek(day)}
      </>
    )
  }

  const buildJapaneseApprovalStatus = (status: string): string => {
    const statusMap: Record<string, string> = {
      completed: '承認済み',
    }
    return statusMap[status] || '未承認'
  }

  const clickDetail = (memberData: DetailParam) => {
    setDetailData(memberData)
    setIsOpenDetailModal(true)
    dispatch(
      fetchTaskApprovalApplicationDetail(
        {
          id: memberData.member_id.toString(),
          target_date: memberData.target_date,
        },
      ),
    )
  }

  const buildStatus = (status: string) => (
    <>
      <div
        className="status_label"
        style={{
          color: labelColor(status),
          border: `solid 1px ${labelColor(status)}`,
          backgroundColor: labelColor(status, true),
          display: "flex",
          alignItems: "center",
          gap: "4px",
          borderRadius: "4px",
          padding: "2px 4px",
          fontSize: "10px",
        }}
      >
        {statusText(status)}
      </div>
    </>
  )

  const ctrlPlusKeyFunction = useCallback((event: KeyboardEvent) => {
    // 「Ctrl + L」キーでリスト表示
    if (event.ctrlKey && event.key === "l") {
      setIsDisplayLiteList(true)
    }
    // 「Ctrl + B」キーでブロック表示
    if (event.ctrlKey && event.key === "b") {
      setIsDisplayLiteList(false)
    }
  }, [])

  const updateApproval = (param: UpdateParam, callback: VoidFunction) => {
    let tmpTarget:{target_date: string, member_id: number, comment: string, start_time?: string, end_time?: string} = {
      target_date: param.target_date,
      member_id: param.member_id,
      comment: param.comment,
    }
    if (param.work_start_time) {
      if (param.work_end_time) {
        tmpTarget = {
          ...tmpTarget,
          start_time: format(param.work_start_time, "YYYY-MM-DD HH:mm:ss"),
          end_time: format(param.work_end_time, "YYYY-MM-DD HH:mm:ss"),
        }
      } else {
        tmpTarget = {
          ...tmpTarget,
          start_time: format(param.work_start_time, "YYYY-MM-DD HH:mm:ss"),
        }
      }
    }
    const tmpParam = {
      status: param.status,
      targets: [tmpTarget],
    }
    const isComplete = Boolean(param.status === "approved"
      && task.taskApprovalApplicationDetail?.approvers.filter((v) => v.member_id === login.current_member_id && !v.priority).length)
    dispatch(
      createTaskApprovalApplication(
        tmpParam,
        isComplete,
        () => {
          callback()
          alert.success(`${format(param.target_date, "YYYY年M月D日")}の申請データを「${statusText(param.status)}」に変更しました`)
          if (!date || !member_id) return
          setCheckList([])
          setSelectMemberId(member_id || 0)
          dispatch(
            fetchTaskApprovalApplication({
              start_date: startOfMonth(date, "YYYY-MM-DD"),
              end_date: endOfMonth(date, "YYYY-MM-DD"),
              member_id: member_id || 0,
            }),
          )
          dispatch(
            fetchTaskApprovalApplicationDetail(
              {
                id: param.member_id.toString(),
                target_date: param.target_date,
              },
            ),
          )
        },
      ),
    )
  }

  const selectMember = (memberId: number) => {
    if (!memberId) return
    setSelectMemberId(memberId)
    setIsOpenSelectMember(false)
    history.push(`${setPath(setUrlId(URL.TASK_APPROVAL_STATUS_DETAIL.path, 'id', memberId), "ja")}?date=${date}`)
  }

  const updateMultiApproval = (param: {target_date_list: string[], comment: string}, callback: VoidFunction) => {
    const tmpParam = {
      status: isAccessSelf ? "processing" : "approved",
      comment: param.comment,
      targets: param.target_date_list.map((v) => (
        {
          target_date: v,
          member_id: member_id || 0,
          comment: null,
        }
      )),
    }
    dispatch(
      createTaskApprovalApplication(
        tmpParam,
        false,
        () => {
          callback()
          alert.success(`チェックした日をまとめて${isAccessSelf ? "申請" : "承認"}しました`)
          resetCheckList()
          dispatch(
            fetchTaskApprovalApplication({
              start_date: startOfMonth(date, "YYYY-MM-DD"),
              end_date: endOfMonth(date, "YYYY-MM-DD"),
              member_id: member_id || 0,
            }),
          )
        },
      ),
    )
  }

  const buildSecToTime = (sec: number) => (
    <>
      {
        !!Number(secToHour(sec)) && (
          <>
            {`${secToHour(sec)}時間`}
          </>
        )
      }
      {`${secToMinute(sec)}分`}
      {`${secToSec(sec)}秒`}
    </>
  )

  const displayTime = (sec: number, size = 16, color = "#CCCCCC") => (
    <>
      {secToTwoDigitsHourMinute(sec)}
      <span
        style={{
          fontSize: `${size}px`,
          color: color,
        }}
      >
        {`:${secToTwoDigitsSec(sec)}`}
      </span>
    </>
  )

  const buildApprovalProgress = (status: string, progress: number, isBlock: boolean) => {
    let color
    let bgColor
    let text
    if (status === "completed") {
      color = "#6c8ebf"
      bgColor = "#dbe8fc"
      text = "承認が完了しています"
    } else if (["unapplied", "returned", "dropped", "canceled"].includes(status)) {
      color = "#ff0000"
      bgColor = "#ffeaea"
      text = "申請されていません"
    } else if (["processing", "approved"].includes(status)) {
      if (progress) {
        color = "#fffeea"
        bgColor = "#d47e13"
        text = `${progress}次承認が完了しています`
      } else {
        color = "#d47e13"
        bgColor = "#fffeea"
        text = "誰にも承認されていません"
      }
    }
    if (!text) return <></>
    return (
      <div
        style={{
          marginBottom: isBlock ? "8px" : 0,
          backgroundColor: bgColor,
          border: `solid 1px ${color}`,
          fontSize: "10px",
          padding: "2px 8px",
          textAlign: "center",
          color: color,
          width: isBlock ? "100%" : "fit-content",
        }}
      >
        {text}
      </div>
    )
  }

  const selectSummaryType = (selectStatuses: string[]) => {
    setSummarySelect(selectStatuses)
    setIsOpenSummarySelect(false)
  }

  const displayWorkingTime = (startTime: string | null, endTime: string | null, targetDate: string) => {
    if (!startTime || !endTime) return "-"
    const start = secToHourMinute(dateToTodayElapsedSeconds(startTime || nowDate(), targetDate))
    const end = secToHourMinute(dateToTodayElapsedSeconds(endTime || nowDate(), targetDate))
    return `${start} ~ ${end}`
  }

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    document.querySelector('html')?.classList.add('scroll_contents')
    document.addEventListener("keydown", ctrlPlusKeyFunction)
    return () => {
      document.removeEventListener("keydown", ctrlPlusKeyFunction)
    }
  }, [])

  useEffect(() => {
    if (!date || !member_id) return
    setCheckList([])
    setSelectMemberId(member_id || 0)
    dispatch(
      fetchTaskApprovalApplication({
        start_date: startOfMonth(date, "YYYY-MM-DD"),
        end_date: endOfMonth(date, "YYYY-MM-DD"),
        member_id: member_id || 0,
      }),
    )
  }, [date, member_id])

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.date) {
      setDate(format(getParams.date, "YYYY-MM-DD"))
      return
    }
    setDate(format(current.targetDate, "YYYY-MM-DD") || nowDate("YYYY-MM-DD"))
  }, [])

  useEffect(() => {
    resetCheckList()
  }, [location])

  return (
    <section className="task_page">
      <Helmet>
        {
          isAccessSelf ? (
            <title>Qasee - 工数管理 - 申請状況</title>
          ) : (
            <title>{`Qasee - 工数管理 - 承認状況 - ${task.taskApprovalApplication && task.taskApprovalApplication?.members[0].name}さんの承認状況`}</title>
          )
        }
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        {
          isAccessSelf ? (
            <>
              申請状況
              &nbsp;
            </>
          ) : (
            <>
              <Link
                to={setPath(URL.TASK_APPROVAL_STATUS.path, "ja")}
              >
                承認状況
              </Link>
              &nbsp;&gt;&nbsp;
              {task.taskApprovalApplication && `${task.taskApprovalApplication?.members[0].name}さんの承認状況`}
              &nbsp;
            </>
          )
        }
      </section>
      <section className="page_header">
        <h1>
          {
            isAccessSelf ? (
              <>
                申請状況
                <UserGuide
                  minHeight="465px"
                  defaultOpen={!current.taskApplication}
                  callback={() => dispatch(updateTaskApplication(true))}
                >
                  <>
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "24px",
                        fontWeight: "bold",
                        marginBottom: "16px",
                        color: "#395F99",
                      }}
                    >
                      申請・承認機能で仕分けデータがそのまま勤怠データに
                    </div>
                    <div
                      style={{
                        width: "100%",
                        height: "300px",
                        backgroundColor: "#FAFAFA",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src="/static/images/task/application/image_1.png"
                        alt="申請・承認機能で仕分けデータがそのまま勤怠データに"
                      />
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#666666",
                      }}
                    >
                      <p>
                        申請・承認機能を使用すると
                        <strong>仕分けデータをそのまま勤怠データ</strong>
                        として活用できます！
                      </p>
                      <p>
                        自動収集された情報とユーザーの入力補完によって、より正確なデータを勤怠データとして保管することが可能です。
                      </p>
                      <p>
                        また、工数管理と勤怠管理が同時におこなえるので、労務の負担を軽減することが期待できます。
                      </p>
                      <p>
                        さらに、申請・承認時にメッセージが入力できるので、日報のように利用することもできます。
                      </p>
                    </div>
                  </>
                  <>
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "24px",
                        fontWeight: "bold",
                        marginBottom: "16px",
                        color: "#395F99",
                      }}
                    >
                      ユーザーの負担を減らします
                    </div>
                    <div
                      style={{
                        width: "100%",
                        height: "300px",
                        backgroundColor: "#FAFAFA",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src="/static/images/task/application/image_2.png"
                        alt="ユーザーの負担を減らします"
                      />
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#666666",
                      }}
                    >
                      <p>
                        工数管理データと勤怠管理データを別々に管理するしかなかった会社も少なくありませんでした。。。
                      </p>
                      <p>
                        これからは
                        <strong>仕分けをするだけで勤怠データも作成できる</strong>
                        ようになり、ユーザーの負担を
                        <strong>大きく削減する</strong>
                        ことができます。
                      </p>
                      <p>
                        仕分けデータと勤怠データのズレもなく、より正確なデータとして保管・活用できます。
                      </p>
                    </div>
                  </>
                  <>
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "24px",
                        fontWeight: "bold",
                        marginBottom: "16px",
                        color: "#395F99",
                      }}
                    >
                      申請・承認機能を利用するには
                    </div>
                    <div
                      style={{
                        width: "100%",
                        height: "300px",
                        backgroundColor: "#FAFAFA",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src="/static/images/task/application/image_3.png"
                        alt="申請・承認機能を利用するには"
                      />
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#666666",
                      }}
                    >
                      <p>
                        まずは「承認グループ」を作成しましょう！
                        <br />
                        承認者と申請者を指定して、”誰”が”誰”に承認されるか設定しましょう。
                      </p>
                      <p>
                        申請者に設定されたメンバーは仕分けデータの申請ができるようになります。
                        <br />
                        また、承認者に設定されたメンバーは申請を”承認”または”差戻し”することができます。
                      </p>
                      <p>
                        最終承認された仕分けデータはロックされ、勤怠データとして利用することができます。
                      </p>
                    </div>
                  </>
                  <>
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "24px",
                        fontWeight: "bold",
                        marginBottom: "16px",
                        color: "#395F99",
                      }}
                    >
                      勤怠管理もQaseeにおまかせ
                    </div>
                    <div
                      style={{
                        width: "100%",
                        height: "300px",
                        backgroundColor: "#FAFAFA",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src="/static/images/task/application/image_4.png"
                        alt="勤怠管理もQaseeにおまかせ"
                      />
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#666666",
                      }}
                    >
                      <p>
                        工数管理も勤怠管理も簡単入力で一元管理！
                      </p>
                      <p>
                        Qaseeは正確なデータであなたの仕事を強力にサポートします。
                      </p>
                    </div>
                  </>
                </UserGuide>
                <VideoGuide
                  videoId="XuymjbqOW6k"
                />
              </>
            ) : (
              <>
                {task.taskApprovalApplication && `${task.taskApprovalApplication?.members[0].name}さんの承認状況`}
              </>
            )
          }
        </h1>
        <div className="tools">
          {
            isAccessSelf || (
              <>
                <div
                  style={{
                    position: "relative",
                  }}
                >
                  <button
                    type="button"
                    className="member_select_box"
                    onClick={() => setIsOpenSelectMember(true)}
                  >
                    {task.taskApprovalApplication?.list_members.find((v) => v.member_id === selectMemberId)?.name}
                    <FontAwesomeIcon icon={['fas', 'angle-down']} style={{ fontSize: "16px" }} />
                  </button>
                  {
                    isOpenSelectMember && (
                      <>
                        <div
                          style={{
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            backgroundColor: "#FFFFFF",
                            padding: "8px",
                            display: "flex",
                            flexFlow: "column",
                            gap: "8px",
                            position: "absolute",
                            width: "100%",
                            zIndex: 2101,
                            fontSize: "14px",
                            maxHeight: "150px",
                            overflowY: "scroll",
                          }}
                        >
                          {
                            task.taskApprovalApplication?.list_members && task.taskApprovalApplication.list_members.map((v) => (
                              <div
                                key={`${v.name}_${v.member_id}`}
                                onClick={() => selectMember(v.member_id)}
                                role="none"
                                style={{
                                  cursor: "pointer",
                                }}
                              >
                                {v.name}
                              </div>
                            ))
                          }
                        </div>
                        <div className="close_screen gray" role="presentation" onClick={() => setIsOpenSelectMember(false)} />
                      </>
                    )
                  }
                </div>
              </>
            )
          }
          <DateModule
            date={date}
            startMonth="202405"
            changeCallback={(callback_date) => {
              setDate(callback_date)
              history.push(`${location.pathname}?date=${callback_date}`)
            }}
          />
        </div>
      </section>
      <section className="page_description">
        <p>
          日々の仕分けデータを承認フローを用いて申請・承認することができます。
        </p>
      </section>
      <section
        className="page_header"
        style={{
          marginBottom: "16px",
          marginTop: "-24px",
        }}
      >
        <div />
        <div
          style={{
            display: "flex",
            gap: "16px",
          }}
        >
          {
            task.taskApprovalApplication && !!task.taskApprovalApplication?.members.length && (
              <>
                <CsvModule
                  title={
                    `勤怠データ ${task.taskApprovalApplication.members[0].name} 【${format(date, "YYYY年MM月")}】`
                  }
                  labels={{
                    member_code: "社員コード",
                    member_name: "メンバー",
                    member_email: "メールアドレス",
                    date_label: "日付",
                    weekday: "曜日",
                    start_date: "勤務開始時刻",
                    end_date: "勤務終了時刻",
                    work_sec: "勤務時間(秒)",
                    work: "勤務時間(hh:mm:ss)",
                    real_work_sec: "実労働時間(秒)",
                    real_work: "実労働時間(hh:mm:ss)",
                    break_sec: "休憩時間(秒)",
                    break: "休憩時間(hh:mm:ss)",
                    over_sec: "残業時間(秒)",
                    over: "残業時間(hh:mm:ss)",
                    work_location_status: "リモート勤務 or オフィス勤務",
                    approval_status: "承認状況",
                    team_name: "チーム",
                  }}
                  list={
                    targetDataList.filter((v) => (
                      format(v.date, "MM") === format((date || nowDate()), "MM")
                      && v.labeling_status.start_time && v.labeling_status.end_time
                    )).map((v) => (
                      {
                        member_code: v.member_code || "",
                        member_name: v.member_name,
                        member_email: v.member_email,
                        date_label: v.date,
                        start_date: format(v.labeling_status.start_time, "HH:mm"),
                        end_date: format(v.labeling_status.end_time, "HH:mm"),
                        // eslint-disable-next-line max-len
                        work_sec: dateToTodayElapsedSeconds(v.labeling_status.end_time || "0", date) - dateToTodayElapsedSeconds(v.labeling_status.start_time || "0", date),
                        // eslint-disable-next-line max-len
                        work: buildWorkTime(dateToTodayElapsedSeconds(v.labeling_status.end_time || "0", date) - dateToTodayElapsedSeconds(v.labeling_status.start_time || "0", date)),
                        weekday: v.weekday,
                        work_location_status: v.work_location_status,
                        // eslint-disable-next-line max-len
                        approval_status: buildJapaneseApprovalStatus(v.approval_status),
                        // eslint-disable-next-line max-len
                        real_work_sec: dateToTodayElapsedSeconds(v.labeling_status.end_time || "0", date) - dateToTodayElapsedSeconds(v.labeling_status.start_time || "0", date) - v.labeling_status.break_time,
                        // eslint-disable-next-line max-len
                        real_work: buildWorkTime(dateToTodayElapsedSeconds(v.labeling_status.end_time || "0", date) - dateToTodayElapsedSeconds(v.labeling_status.start_time || "0", date) - v.labeling_status.break_time),
                        break_sec: v.labeling_status.break_time,
                        break: buildWorkTime(v.labeling_status.break_time),
                        over_sec: v.labeling_status.over_time,
                        over: buildWorkTime(v.labeling_status.over_time),
                        team_name: v.team_name || "",
                      }
                    ))
                  }
                  button_text="勤怠データをダウンロードする"
                />
              </>
            )
          }
        </div>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="changeable_table_header">
              <div className="title" style={{ margin: 0 }}>
                {
                  isAccessSelf ? (
                    <>
                      申請状況
                    </>
                  ) : (
                    <>
                      {task.taskApprovalApplication && `${task.taskApprovalApplication?.members[0].name}さんの承認状況`}
                    </>
                  )
                }
                {`【${format(date, "YYYY年M月")}】`}
              </div>
              <div className="button_area">
                {
                  isDisplayLiteList ? (
                    <>
                      <Tips
                        description="ブロック表示に切り替えます"
                        className=""
                        icon={false}
                        key={uniqueId()}
                      >
                        <>
                          <button
                            type="button"
                            onClick={toggleListView}
                          >
                            <FontAwesomeIcon icon={['fas', 'list']} />
                            <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                            <FontAwesomeIcon icon={['fas', 'chart-pie']} />
                          </button>
                        </>
                      </Tips>
                    </>
                  ) : (
                    <>
                      <Tips
                        description="リスト表示に切り替えます"
                        className=""
                        icon={false}
                        key={uniqueId()}
                      >
                        <>
                          <button
                            type="button"
                            onClick={toggleListView}
                          >
                            <FontAwesomeIcon icon={['fas', 'chart-pie']} />
                            <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                            <FontAwesomeIcon icon={['fas', 'list']} />
                          </button>
                        </>
                      </Tips>
                    </>
                  )
                }
              </div>
            </div>
            <div className="button_control_area">
              {
                task.taskApprovalApplication
                && !!task.taskApprovalApplication?.members.length
                && task.taskApprovalApplication?.members[0].application_possible && (
                  <>
                    <button
                      type="button"
                      style={{
                        color: labelColor("approved"),
                        border: `solid 1px ${labelColor("approved")}`,
                        backgroundColor: labelColor("approved", true),
                      }}
                      onClick={() => checkAll()}
                    >
                      <FontAwesomeIcon icon={['fas', 'check-square']} />
                      {`${isAccessSelf ? "申請可能" : "申請中"}データをすべてチェックする`}
                    </button>
                    <button
                      type="button"
                      onClick={() => resetCheckList()}
                    >
                      <FontAwesomeIcon icon={['fas', 'times']} />
                      すべてのチェックを外す
                    </button>
                  </>
                )
              }
            </div>
          </div>
          {
            task.taskApprovalApplication
            && !!task.taskApprovalApplication?.members.length
            && task.taskApprovalApplication?.members[0].application_possible && (
              <>
                <div className="application_control_area">
                  <button
                    type="button"
                    style={{
                      color: checkList.length ? labelColor("approved") : "#CCCCCC",
                      border: `solid 1px ${checkList.length ? labelColor("approved") : "#CCCCCC"}`,
                      backgroundColor: checkList.length ? labelColor("approved", true) : "#EEEEEE",
                    }}
                    disabled={!checkList.length}
                    onClick={() => setIsOpenMultiApprovalModal(true)}
                  >
                    {`チェックした日をまとめて${isAccessSelf ? "申請" : "承認"}する`}
                  </button>
                </div>
              </>
            )
          }
          <div className="summary_view_area">
            <div className="summary_view_control_area">
              {format(date, "YYYY年MM月")}
              <div className="summary_select_area_wrapper">
                <div
                  className="summary_select_area"
                  role="none"
                  onClick={() => setIsOpenSummarySelect(!isOpenSummarySelect)}
                >
                  {
                    summarySelect.map((v) => (
                      <React.Fragment key={`summary_select_${v}`}>
                        {buildStatus(v)}
                      </React.Fragment>
                    ))
                  }
                  <FontAwesomeIcon icon={['fas', 'caret-down']} />
                </div>
                {
                  isOpenSummarySelect && (
                    <>
                      <div className="summary_type_list">
                        {
                          SUMMARY_TYPE_LIST.map((v) => (
                            <div
                              key={uniqueId()}
                              className={`summary_type${summarySelect.toString() === v.toString() ? " active" : ""}`}
                              onClick={() => selectSummaryType(v)}
                              role="list"
                            >
                              {
                                v.map((w) => (
                                  <React.Fragment key={uniqueId()}>
                                    {buildStatus(w)}
                                  </React.Fragment>
                                ))
                              }
                              の集計情報
                            </div>
                          ))
                        }
                      </div>
                      <div
                        className="summary_type_list_screen"
                        onClick={() => setIsOpenSummarySelect(false)}
                        role="presentation"
                      />
                    </>
                  )
                }
              </div>
              の集計情報
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Tips
                  description="申請・承認された日数 一ヶ月の合計"
                  className=""
                  icon={false}
                >
                  <>
                    <div
                      style={{
                        backgroundColor: "#025393",
                        color: "#ffffff",
                        fontSize: "10px",
                        borderRadius: "4px",
                        padding: "2px 4px",
                      }}
                    >
                      日数
                    </div>
                  </>
                </Tips>
                <div
                  style={{
                    fontSize: "16px",
                  }}
                >
                  {`${(targetMonthSummary.day_count || 0)}日`}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Tips
                  description="勤務開始~終了時間から休憩時間を引いた時間 一ヶ月の合計時間"
                  className=""
                  icon={false}
                >
                  <>
                    <div
                      style={{
                        backgroundColor: "#2196f3",
                        color: "#ffffff",
                        fontSize: "10px",
                        borderRadius: "4px",
                        padding: "2px 4px",
                      }}
                    >
                      総実労働時間
                    </div>
                  </>
                </Tips>
                <div
                  style={{
                    fontSize: "16px",
                  }}
                >
                  {buildSecToTime(targetMonthSummary.work_time || 0)}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Tips
                  description="休憩ラベルをつけた作業データ 一ヶ月の合計時間"
                  className=""
                  icon={false}
                >
                  <>
                    <div
                      style={{
                        backgroundColor: "#e87f43",
                        color: "#ffffff",
                        fontSize: "10px",
                        borderRadius: "4px",
                        padding: "2px 4px",
                      }}
                    >
                      総休憩時間
                    </div>
                  </>
                </Tips>
                <div
                  style={{
                    fontSize: "16px",
                  }}
                >
                  {buildSecToTime(targetMonthSummary.break_time || 0)}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Tips
                  description="実労働時間から標準勤務時間を引いた時間 一ヶ月の合計時間"
                  className=""
                  icon={false}
                >
                  <>
                    <div
                      style={{
                        backgroundColor: "#7c0000",
                        color: "#ffffff",
                        fontSize: "10px",
                        borderRadius: "4px",
                        padding: "2px 4px",
                      }}
                    >
                      総残業時間
                    </div>
                  </>
                </Tips>
                <div
                  style={{
                    fontSize: "16px",
                  }}
                >
                  {buildSecToTime(targetMonthSummary.over_time || 0)}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Tips
                  description="業務仕分けされた作業データ 一ヶ月の合計時間"
                  className=""
                  icon={false}
                >
                  <>
                    <div
                      style={{
                        backgroundColor: "#109554",
                        color: "#ffffff",
                        fontSize: "10px",
                        borderRadius: "4px",
                        padding: "2px 4px",
                      }}
                    >
                      総仕分け済み時間
                    </div>
                  </>
                </Tips>
                <div
                  style={{
                    fontSize: "16px",
                  }}
                >
                  {buildSecToTime(targetMonthSummary.labeling_time || 0)}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Tips
                  description="業務仕分けされていない作業データ 一ヶ月の合計時間"
                  className=""
                  icon={false}
                >
                  <>
                    <div
                      style={{
                        backgroundColor: "#c61516",
                        color: "#ffffff",
                        fontSize: "10px",
                        borderRadius: "4px",
                        padding: "2px 4px",
                      }}
                    >
                      総未仕分け時間
                    </div>
                  </>
                </Tips>
                <div
                  style={{
                    fontSize: "16px",
                  }}
                >
                  {buildSecToTime(targetMonthSummary.not_labeling_time || 0)}
                </div>
              </div>
            </div>
          </div>
          <div className="block-detail">
            {
              task.isLoading ? (
                <LoadingSquare>
                  <>
                    情報を取得しています…
                  </>
                </LoadingSquare>
              ) : (
                <>
                  {
                    isDisplayLiteList ? (
                      <>
                        <div className="application_list">
                          {
                            targetDataList.filter((v) => (format(v.date, "MM") === format((date || nowDate()), "MM"))).map((v) => (
                              <div
                                className="application_list_row"
                                style={{
                                  backgroundColor: checkList.includes(v.date) ? "#eaffe4" : calendarColor(v.date, true),
                                }}
                                onClick={() => {
                                  if (Number(format(v.date, "YYYYMMDD")) <= Number(nowDate("YYYYMMDD"))
                                    && Number(format(v.date, "YYYYMMDD")) > 20240430
                                    && task.taskApprovalApplication?.members[0].application_possible
                                    && (
                                      !!v.labeling_status.labeling
                                      || !!v.labeling_status.not_labeling
                                    ) && (
                                    (
                                      !isAccessSelf
                                      && v.approval_status === "processing"
                                    ) || (
                                      isAccessSelf
                                      && (
                                        (v.approval_status === "unapplied")
                                        || (v.approval_status === "returned")
                                        || (v.approval_status === "dropped")
                                        || (v.approval_status === "canceled")
                                      )
                                    )
                                  )) {
                                    toggleCheckDate(v.date)
                                  }
                                }}
                                role="none"
                              >
                                <div className="checkbox_column">
                                  {
                                    Number(format(v.date, "YYYYMMDD")) <= Number(nowDate("YYYYMMDD"))
                                    && Number(format(v.date, "YYYYMMDD")) > 20240430
                                    && task.taskApprovalApplication?.members[0].application_possible
                                    && (
                                      !!v.labeling_status.labeling
                                      || !!v.labeling_status.not_labeling
                                    ) && (
                                      (
                                        !isAccessSelf
                                        && v.approval_status === "processing"
                                      ) || (
                                        isAccessSelf
                                        && (
                                          (v.approval_status === "unapplied")
                                          || (v.approval_status === "returned")
                                          || (v.approval_status === "dropped")
                                          || (v.approval_status === "canceled")
                                        )
                                      )
                                    ) && (
                                      <div>
                                        {
                                          checkList.includes(v.date) ? (
                                            <FontAwesomeIcon
                                              icon={['far', 'check-square']}
                                              style={{
                                                backgroundColor: "#ffffff",
                                                color: "#0f9655",
                                              }}
                                            />
                                          ) : (
                                            <FontAwesomeIcon icon={['far', 'square']} style={{ backgroundColor: "#ffffff" }} />
                                          )
                                        }
                                      </div>
                                    )
                                  }
                                </div>
                                <div className="name_column">
                                  <div className="up_area">
                                    <div>
                                      {buildDate(v.date)}
                                    </div>
                                    {
                                      Number(format(v.date, "YYYYMMDD")) <= Number(nowDate("YYYYMMDD"))
                                      && Number(format(v.date, "YYYYMMDD")) > 20240430
                                      && !!v.approval_status && (
                                        <>
                                          {buildStatus(v.approval_status)}
                                        </>
                                      )
                                    }
                                    {
                                      v.labeling_status.changed && (
                                        <Tips
                                          description="勤務開始・終了時間に関する変更がおこなわれています"
                                          className=""
                                          icon={false}
                                          style={{
                                            color: "#c1a50d",
                                            height: "auto",
                                          }}
                                        >
                                          <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
                                        </Tips>
                                      )
                                    }
                                  </div>
                                  {buildApprovalProgress(v.approval_status, v.approval_progress, false)}
                                </div>
                                {
                                  // eslint-disable-next-line max-len
                                  Number(format(v.date, "YYYYMMDD")) <= Number(nowDate("YYYYMMDD")) && Number(format(v.date, "YYYYMMDD")) > 20240430 && !!v.approval_status ? (
                                    <div className="main_column">
                                      <div className="main_column_left">
                                        <div className="up_area">
                                          <div
                                            className="title_label"
                                            style={{
                                              backgroundColor: "#3f51b5",
                                            }}
                                          >
                                            勤務時間
                                          </div>
                                          <div className="value">
                                            {/* eslint-disable-next-line max-len */}
                                            {displayWorkingTime(v.labeling_status.start_time, v.labeling_status.end_time, v.date)}
                                          </div>
                                        </div>
                                        <div className="up_area">
                                          <Tips
                                            description="勤務開始~終了時間から休憩時間を引いた時間"
                                            className=""
                                            icon={false}
                                          >
                                            <>
                                              <div
                                                className="title_label"
                                                style={{
                                                  backgroundColor: "#2196f3",
                                                }}
                                              >
                                                実労働時間
                                              </div>
                                            </>
                                          </Tips>
                                          <div className="value min">
                                            {
                                              v.labeling_status.total_work_time ? (
                                                <>
                                                  {buildSecToTime(v.labeling_status.total_work_time)}
                                                </>
                                              ) : (
                                                <>
                                                  なし
                                                </>
                                              )
                                            }
                                          </div>
                                        </div>
                                        <div className="down_area">
                                          <Tips
                                            description="休憩ラベルをつけた作業データの合計時間"
                                            className=""
                                            style={{ zIndex: 2010 }}
                                            icon={false}
                                          >
                                            <>
                                              <div
                                                className="title_label"
                                                style={{
                                                  backgroundColor: "#e88042",
                                                }}
                                              >
                                                休憩時間
                                              </div>
                                            </>
                                          </Tips>
                                          <div className="value min">
                                            {
                                              v.labeling_status.break_time ? (
                                                <>
                                                  {buildSecToTime(v.labeling_status.break_time)}
                                                </>
                                              ) : (
                                                <>
                                                  休憩なし
                                                </>
                                              )
                                            }
                                          </div>
                                        </div>
                                      </div>
                                      <div className="main_column_right">
                                        <div className="main_column_contents_wrapper">
                                          <div className="detail_area">
                                            <div className="detail_block">
                                              <div
                                                className="label"
                                                style={{
                                                  backgroundColor: "#119555",
                                                  color: "#FFFFFF",
                                                }}
                                              >
                                                仕分け済み
                                              </div>
                                              <div>
                                                {displayTime(v.labeling_status.labeling, 12)}
                                              </div>
                                            </div>
                                            <div className="detail_block">
                                              <div
                                                className="label"
                                                style={{
                                                  backgroundColor: "#c61616",
                                                  color: "#FFFFFF",
                                                }}
                                              >
                                                未仕分け
                                              </div>
                                              <div>
                                                {displayTime(v.labeling_status.not_labeling, 12)}
                                              </div>
                                            </div>
                                          </div>
                                          <div className="bar_area">
                                            {
                                              v.labeling_status.labeling ? (
                                                <>
                                                  <Tips
                                                    // eslint-disable-next-line max-len
                                                    description={`仕分け済み:${Math.round((v.labeling_status.labeling / (v.labeling_status.labeling + v.labeling_status.not_labeling)) * 100)}%%br%(${buildWorkTime(v.labeling_status.labeling)})`}
                                                    className=""
                                                    icon={false}
                                                    style={{
                                                      // eslint-disable-next-line max-len
                                                      width: `${(v.labeling_status.labeling / (v.labeling_status.labeling + v.labeling_status.not_labeling)) * 100}%`,
                                                    }}
                                                  >
                                                    <div
                                                      className="bar"
                                                      style={{
                                                        backgroundColor: "#0D9554",
                                                        color: "#FFFFFF",
                                                      }}
                                                    >
                                                      {
                                                        // eslint-disable-next-line max-len
                                                        (v.labeling_status.labeling / (v.labeling_status.labeling + v.labeling_status.not_labeling)) * 100 > 6 && (
                                                          <>
                                                            {/* eslint-disable-next-line max-len */}
                                                            {`${Math.round((v.labeling_status.labeling / (v.labeling_status.labeling + v.labeling_status.not_labeling)) * 100)}%`}
                                                          </>
                                                        )
                                                      }
                                                    </div>
                                                  </Tips>
                                                  <Tips
                                                    // eslint-disable-next-line max-len
                                                    description={`未仕分け:${Math.round((v.labeling_status.not_labeling / (v.labeling_status.labeling + v.labeling_status.not_labeling)) * 100)}%%br%(${buildWorkTime(v.labeling_status.not_labeling)})`}
                                                    className=""
                                                    icon={false}
                                                    style={{
                                                      // eslint-disable-next-line max-len
                                                      width: `${(v.labeling_status.not_labeling / (v.labeling_status.labeling + v.labeling_status.not_labeling)) * 100}%`,
                                                    }}
                                                  >
                                                    <div
                                                      className="bar"
                                                      style={{
                                                        backgroundColor: "#c61616",
                                                        color: "#FFFFFF",
                                                      }}
                                                    >
                                                      {
                                                        // eslint-disable-next-line max-len
                                                        (v.labeling_status.not_labeling / (v.labeling_status.labeling + v.labeling_status.not_labeling)) * 100 > 6 && (
                                                          <>
                                                            {/* eslint-disable-next-line max-len */}
                                                            {`${Math.round((v.labeling_status.not_labeling / (v.labeling_status.labeling + v.labeling_status.not_labeling)) * 100)}%`}
                                                          </>
                                                        )
                                                      }
                                                    </div>
                                                  </Tips>
                                                </>
                                              ) : (
                                                <>
                                                  <Tips
                                                    description="仕分けデータなし"
                                                    className=""
                                                    icon={false}
                                                    style={{
                                                      width: "100%",
                                                    }}
                                                  >
                                                    <div
                                                      className="bar"
                                                      style={{
                                                        backgroundColor: "#CCCCCC",
                                                        color: "#FFFFFF",
                                                      }}
                                                    >
                                                      仕分けデータなし
                                                    </div>
                                                  </Tips>
                                                </>
                                              )
                                            }
                                          </div>
                                        </div>
                                      </div>
                                      <div className="main_column_button">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            clickDetail({
                                              member_id: v.member_id,
                                              member_name: v.member_name,
                                              target_date: v.date,
                                            })
                                          }}
                                        >
                                          詳細を確認する
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="main_column no_data">
                                      データがありません
                                    </div>
                                  )
                                }
                              </div>
                            ))
                          }
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="application_date_wrapper">
                          {
                            targetDataList.map((v) => (
                              <React.Fragment key={v.date}>
                                {
                                  ((format(v.date, "MM") === format((date || nowDate()), "MM")
                                    && Number(format(v.date, "YYYYMMDD")) <= Number(nowDate("YYYYMMDD")))) ? (
                                      <div
                                        className={`date ${Number(format(v.date, "YYYYMMDD")) === Number(nowDate("YYYYMMDD")) ? "today" : ""}`}
                                        style={{
                                          backgroundColor: checkList.includes(v.date) ? "#eaffe4" : calendarColor(v.date, true),
                                        }}
                                        onClick={() => {
                                          if (Number(format(v.date, "YYYYMMDD")) <= Number(nowDate("YYYYMMDD"))
                                            && Number(format(v.date, "YYYYMMDD")) > 20240430
                                            && task.taskApprovalApplication?.members[0].application_possible
                                            && (
                                              !!v.labeling_status.labeling
                                              || !!v.labeling_status.not_labeling
                                            ) && (
                                            (
                                              !isAccessSelf
                                              && v.approval_status === "processing"
                                            ) || (
                                              isAccessSelf
                                              && (
                                                (v.approval_status === "unapplied")
                                                || (v.approval_status === "returned")
                                                || (v.approval_status === "dropped")
                                                || (v.approval_status === "canceled")
                                              )
                                            )
                                          )) {
                                            toggleCheckDate(v.date)
                                          }
                                        }}
                                        role="none"
                                      >
                                        <div className="date_header">
                                          <div className="date_header_left">
                                            {
                                              Number(format(v.date, "YYYYMMDD")) <= Number(nowDate("YYYYMMDD"))
                                              && Number(format(v.date, "YYYYMMDD")) > 20240430
                                              && task.taskApprovalApplication?.members[0].application_possible
                                              && (
                                                !!v.labeling_status.labeling
                                                || !!v.labeling_status.not_labeling
                                              ) && (
                                                (
                                                  !isAccessSelf
                                                  && v.approval_status === "processing"
                                                ) || (
                                                  isAccessSelf
                                                  && (
                                                    (v.approval_status === "unapplied")
                                                    || (v.approval_status === "returned")
                                                    || (v.approval_status === "dropped")
                                                    || (v.approval_status === "canceled")
                                                  )
                                                )
                                              ) && (
                                                <div
                                                  style={{
                                                    fontSize: "15px",
                                                  }}
                                                >
                                                  {
                                                    checkList.includes(v.date) ? (
                                                      <FontAwesomeIcon
                                                        icon={['far', 'check-square']}
                                                        style={{
                                                          backgroundColor: "#ffffff",
                                                          color: "#0f9655",
                                                        }}
                                                      />
                                                    ) : (
                                                      <FontAwesomeIcon icon={['far', 'square']} style={{ backgroundColor: "#ffffff" }} />
                                                    )
                                                  }
                                                </div>
                                              )
                                            }
                                            <div
                                              style={{
                                                color: calendarColor(v.date),
                                              }}
                                            >
                                              {format(v.date, "M/D")}
                                            </div>
                                            {
                                              v.labeling_status.changed && (
                                                <Tips
                                                  description="勤務開始・終了時間に関する変更がおこなわれています"
                                                  className=""
                                                  icon={false}
                                                  style={{
                                                    color: "#c1a50d",
                                                    height: "auto",
                                                  }}
                                                >
                                                  <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />
                                                </Tips>
                                              )
                                            }
                                          </div>
                                          {
                                            !!v.approval_status && Number(format(v.date, "YYYYMMDD")) > 20240430 && (
                                              <>
                                                {buildStatus(v.approval_status)}
                                              </>
                                            )
                                          }
                                        </div>
                                        <div>
                                          {
                                            v.approval_status && Number(format(v.date, "YYYYMMDD")) > 20240430 ? (
                                              <>
                                                {buildApprovalProgress(v.approval_status, v.approval_progress, true)}
                                                <div className="work_time">
                                                  <div>
                                                    <div
                                                      className="label_chip"
                                                      style={{
                                                        backgroundColor: "#3f51b5",
                                                      }}
                                                    >
                                                      勤務
                                                    </div>
                                                    {displayWorkingTime(v.labeling_status.start_time, v.labeling_status.end_time, v.date)}
                                                  </div>
                                                </div>
                                                <div className="work_time">
                                                  <div>
                                                    <Tips
                                                      description="勤務開始~終了時間から休憩時間を引いた時間"
                                                      className=""
                                                      icon={false}
                                                    >
                                                      <>
                                                        <div
                                                          className="label_chip"
                                                          style={{
                                                            backgroundColor: "#2196f3",
                                                          }}
                                                        >
                                                          実労働
                                                        </div>
                                                      </>
                                                    </Tips>
                                                    {
                                                      v.labeling_status.total_work_time ? (
                                                        <>
                                                          {secToHourMinute(v.labeling_status.total_work_time)}
                                                        </>
                                                      ) : (
                                                        <>
                                                          なし
                                                        </>
                                                      )
                                                    }
                                                  </div>
                                                  <div>
                                                    <Tips
                                                      description="休憩ラベルをつけた作業データの合計時間"
                                                      className=""
                                                      icon={false}
                                                    >
                                                      <>
                                                        <div
                                                          className="label_chip"
                                                          style={{
                                                            backgroundColor: "#e88042",
                                                          }}
                                                        >
                                                          休憩
                                                        </div>
                                                      </>
                                                    </Tips>
                                                    {
                                                      v.labeling_status.break_time ? (
                                                        <>
                                                          {secToHourMinute(v.labeling_status.break_time)}
                                                        </>
                                                      ) : (
                                                        <>
                                                          休憩なし
                                                        </>
                                                      )
                                                    }
                                                  </div>
                                                </div>
                                                <div className="graph_area">
                                                  <PieChart
                                                    list={[
                                                      {
                                                        label: "",
                                                        value: v.labeling_status.labeling,
                                                        color: "#0D9554",
                                                      },
                                                      {
                                                        label: "",
                                                        value: v.labeling_status.not_labeling,
                                                        color: "#c61616",
                                                      },
                                                    ]}
                                                    chartSize={80}
                                                  />
                                                  <div className="text_area">
                                                    <div className="text_area_title">
                                                      仕分け率
                                                    </div>
                                                    <div className="text_area_text">
                                                      {
                                                        v.labeling_status.labeling ? (
                                                          <>
                                                            {/* eslint-disable-next-line max-len */}
                                                            {`${Math.floor((v.labeling_status.labeling / (v.labeling_status.labeling + v.labeling_status.not_labeling)) * 100)}%`}
                                                          </>
                                                        ) : (
                                                          <>
                                                            0%
                                                          </>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                </div>
                                                <button
                                                  type="button"
                                                  className="detail_button"
                                                  onClick={(e) => {
                                                    e.stopPropagation()
                                                    clickDetail({
                                                      member_id: v.member_id,
                                                      member_name: v.member_name,
                                                      target_date: v.date,
                                                    })
                                                  }}
                                                >
                                                  詳細を確認する
                                                </button>
                                              </>
                                            ) : (
                                              <div className="no_data">
                                                データがありません
                                              </div>
                                            )
                                          }
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="date disable">
                                        <div
                                          style={{
                                            color: calendarColor(v.date),
                                          }}
                                        >
                                          {format(v.date, "M/D")}
                                        </div>
                                      </div>
                                    )
                                }
                              </React.Fragment>
                            ))
                          }
                        </div>
                      </>
                    )
                  }
                </>
              )
            }
          </div>
        </div>
      </section>
      <ApprovedModal
        detail={detailData}
        closeCallback={() => setIsOpenDetailModal(false)}
        updateCallback={(param, callback) => updateApproval(param, callback)}
        isDisplay={isOpenDetailModal}
        isApplication={isAccessSelf}
        moveDateCallback={(addNum) => clickDetail({ ...detailData, target_date: dateManipulateDay(detailData.target_date, addNum, "YYYY-MM-DD") })}
        isDisplayWorkTime={!isAccessSelf}
      />
      <MultiApprovedModal
        targetDateList={checkList}
        closeCallback={() => setIsOpenMultiApprovalModal(false)}
        updateCallback={(param, callback) => updateMultiApproval(param, callback)}
        isDisplay={isOpenMultiApprovalModal}
        isApplication={isAccessSelf}
      />
    </section>
  )
}

export default Detail
